export const QUICK_ACTIONS_LEGEND_ROW_ID = "__quick_actions_legend__";

export const RELEASE_STATUS_FILTER_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "ready", label: "Ready" },
  { value: "pending", label: "Pending" },
  { value: "admin_approval", label: "Admin Approval" },
  { value: "approval", label: "Submission Queued" },
  { value: "delivering", label: "Delivering" },
  { value: "distributed", label: "Distributed" },
  { value: "locked", label: "In Progress - Locked" },
  { value: "re_delivery-delivery", label: "Re-delivery" },
  { value: "re_delivery-editing", label: "Re-delivery Editing" },
  { value: "takedown_request", label: "Takedown Request" },
  { value: "taken_down", label: "Taken Down" },
  { value: "offline", label: "Offline" },
];

export const getReleaseListStatusQuery = (statusFilter) => {
  if (!statusFilter) {
    return "";
  }
  if (statusFilter === "pending") {
    return "&status=ready";
  }
  return `&status=${statusFilter}`;
};

export const CARD_SORT_OPTIONS = [
  { value: "official_date:-", label: "Release Date (Newest)" },
  { value: "official_date:", label: "Release Date (Oldest)" },
  { value: "name:", label: "Name (A-Z)" },
  { value: "name:-", label: "Name (Z-A)" },
  { value: "status:", label: "Status (A-Z)" },
  { value: "status:-", label: "Status (Z-A)" },
  { value: "catalogue_number:", label: "Cat. No. (A-Z)" },
  { value: "catalogue_number:-", label: "Cat. No. (Z-A)" },
];

export const LAYOUT_OPTIONS = [
  { value: "table", label: "Table view" },
  { value: "cards", label: "Card view" },
];

export const TABLE_PAGE_SIZE_OPTIONS = [1, 2, 4, 5, 10, 25, 50];
export const CARD_PAGE_SIZE_OPTIONS = [4, 8, 12, 16, 32];
export const DEFAULT_TABLE_PAGE_SIZE = 10;
export const DEFAULT_CARD_PAGE_SIZE = 4;

export const RELEASE_LIST_LAYOUT_CONFIG_STORAGE_KEY = "wlDemoBrandingListLayout";
export const RELEASE_LIST_LAYOUT_CHANGED_EVENT = "wl-demo-branding-list-layout-updated";

const RELEASE_LIST_LAYOUT_STORAGE_KEY = "releaseListLayout";
const DEFAULT_LIST_LAYOUT_CONFIG = process.env.REACT_APP_BRANDING_LIST_LAYOUT || "table_card";
const VALID_LIST_LAYOUT_CONFIGS = new Set(["table", "card", "table_card"]);

export const getPageSizeOptionsForLayout = (layout) =>
  layout === "cards" ? CARD_PAGE_SIZE_OPTIONS : TABLE_PAGE_SIZE_OPTIONS;

export const getDefaultPageSizeForLayout = (layout) =>
  layout === "cards" ? DEFAULT_CARD_PAGE_SIZE : DEFAULT_TABLE_PAGE_SIZE;

export const getReleaseListLayoutConfig = () => {
  try {
    const stored = localStorage.getItem(RELEASE_LIST_LAYOUT_CONFIG_STORAGE_KEY);
    if (VALID_LIST_LAYOUT_CONFIGS.has(stored)) {
      return stored;
    }
  } catch {
    // localStorage may be unavailable.
  }
  return VALID_LIST_LAYOUT_CONFIGS.has(DEFAULT_LIST_LAYOUT_CONFIG)
    ? DEFAULT_LIST_LAYOUT_CONFIG
    : "table";
};

export const setRuntimeReleaseListLayoutConfig = (layoutConfig) => {
  if (!VALID_LIST_LAYOUT_CONFIGS.has(layoutConfig)) {
    return;
  }
  try {
    localStorage.setItem(RELEASE_LIST_LAYOUT_CONFIG_STORAGE_KEY, layoutConfig);
  } catch {
    // ignore write failures
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(RELEASE_LIST_LAYOUT_CHANGED_EVENT, {
        detail: { layoutConfig },
      }),
    );
  }
};

export const isReleaseLayoutToggleEnabled = (layoutConfig = getReleaseListLayoutConfig()) =>
  layoutConfig === "table_card";

export const getStoredReleaseListLayout = () => {
  try {
    const stored = localStorage.getItem(RELEASE_LIST_LAYOUT_STORAGE_KEY);
    if (stored && LAYOUT_OPTIONS.some(({ value }) => value === stored)) {
      return stored;
    }
  } catch {
    // localStorage may be unavailable.
  }
  return "table";
};

export const setStoredReleaseListLayout = (layout) => {
  try {
    localStorage.setItem(RELEASE_LIST_LAYOUT_STORAGE_KEY, layout);
  } catch {
    // ignore write failures
  }
};

export const resolveReleaseListLayout = (layoutConfig = getReleaseListLayoutConfig()) => {
  if (layoutConfig === "card") return "cards";
  if (layoutConfig === "table_card") return getStoredReleaseListLayout();
  return "table";
};

export const formatCardDate = (dateStr) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

export const formatReleaseKind = (kind) => {
  if (!kind) return "";
  if (kind.toLowerCase() === "ep") return "EP";
  return kind.charAt(0).toUpperCase() + kind.slice(1).toLowerCase();
};

export const resolveReleaseKind = (row = {}) => {
  if (row.kind) {
    return row.kind;
  }

  const trackCount = Number(row.get_mixes_number);
  if (!Number.isFinite(trackCount) || trackCount <= 0) {
    return "";
  }
  if (trackCount === 1) {
    return "single";
  }
  if (trackCount <= 6) {
    return "EP";
  }
  return "album";
};

export const formatTrackCountLabel = (count) => {
  if (count == null) return "";
  return count === 1 ? "1 Track" : `${count} Tracks`;
};

export const getReleaseCardFormatLabel = (row = {}) => {
  const kind = resolveReleaseKind(row);
  const trackLabel = formatTrackCountLabel(row.get_mixes_number);
  return [formatReleaseKind(kind), trackLabel].filter(Boolean).join(" • ");
};

export const getReleaseKindIcon = (kind) => {
  switch ((kind || "").toLowerCase()) {
    case "single":
      return "fa-music";
    case "ep":
      return "fa-list-ol";
    case "album":
      return "fa-circle";
    case "compilation":
      return "fa-boxes";
    default:
      return "fa-music";
  }
};

export const isSubuserRelease = (row, { username } = {}) => {
  if (!row || typeof row !== "object") {
    return false;
  }
  if (row.is_subuser_release === true) {
    return true;
  }
  if (row.sub_user != null && row.sub_user !== "") {
    return true;
  }
  if (row.owner != null && row.owner !== "" && username) {
    return username !== row.owner;
  }
  return false;
};

export const getDisplayStatus = (row, { username, is_premium_user, is_employee_account }) => {
  const showPendingStatus =
    (is_premium_user || is_employee_account) &&
    row.status === "ready" &&
    isSubuserRelease(row, { username });
  return showPendingStatus ? "pending" : row.status;
};

export const filterReleasesByPendingStatus = (results, userContext) =>
  results.filter((row) => getDisplayStatus(row, userContext) === "pending");

export const filterReleasesByReadyStatus = (results, userContext) =>
  results.filter((row) => getDisplayStatus(row, userContext) === "ready");

export const hasQCIssues = (row) =>
  !row.qc_passed &&
  !row.edited_after_qc &&
  row.qc_feedback?.results &&
  Object.keys(row.qc_feedback.results).length !== 0;

export const getStatusBadgeInfo = (status, { release_admin_approval, is_premium_user, is_employee_account }) => {
  if (status === "admin_approval") {
    const isReady = release_admin_approval && (is_premium_user || is_employee_account);
    return {
      label: isReady ? "Ready" : "Submission Queued",
      dotClass: isReady ? "status-dot--info" : "status-dot--warning",
    };
  }

  const statusMap = {
    ready: { label: "Ready", dotClass: "status-dot--info" },
    pending: { label: "Pending", dotClass: "status-dot--warning" },
    approval: { label: "Submission Queued", dotClass: "status-dot--warning" },
    takedown_request: { label: "Takedown request", dotClass: "status-dot--warning" },
    taken_down: { label: "Taken down", dotClass: "status-dot--danger" },
    distributed: { label: "Distributed", dotClass: "status-dot--distributed" },
    locked: { label: "In Progress", dotClass: "status-dot--info" },
    delivering: { label: "Delivering", dotClass: "status-dot--info" },
    unlocked: { label: "Please Edit", dotClass: "status-dot--warning" },
    offline: { label: "Offline", dotClass: "status-dot--danger" },
    "re_delivery-delivery": { label: "Re-delivery", dotClass: "status-dot--warning" },
    "re_delivery-editing": { label: "Re-delivery Editing", dotClass: "status-dot--warning" },
  };

  return statusMap[status] || { label: "Unknown Status", dotClass: "status-dot--muted" };
};

const CARD_ACTION_REQUIRED_STATUSES = new Set([
  "pending",
  "delivering",
  "re_delivery-editing",
  "approval",
]);

export const isCardStatusActionRequired = (
  status,
  { release_admin_approval, is_premium_user, is_employee_account },
) => {
  if (status === "admin_approval") {
    const isReady = release_admin_approval && (is_premium_user || is_employee_account);
    return !isReady;
  }
  return CARD_ACTION_REQUIRED_STATUSES.has(status);
};

const CARD_ACCENT_BY_DOT = {
  "status-dot--distributed": "release-card--accent-distributed",
  "status-dot--info": "release-card--accent-info",
  "status-dot--warning": "release-card--accent-warning",
  "status-dot--danger": "release-card--accent-danger",
  "status-dot--muted": "release-card--accent-muted",
};

export const getReleaseArtworkUrl = (artwork) => {
  if (!artwork) return null;
  return artwork.full_size || artwork.thumb_small || null;
};

export const getCardAccentClass = (status, tenantContext, userContext, qcIssues) => {
  if (qcIssues) {
    return "release-card--accent-danger";
  }
  const { dotClass } = getStatusBadgeInfo(status, {
    release_admin_approval: tenantContext.release_admin_approval,
    is_premium_user: userContext.is_premium_user,
    is_employee_account: userContext.is_employee_account,
  });
  return CARD_ACCENT_BY_DOT[dotClass] || "release-card--accent-muted";
};
